// PDP reviews: star summary (only when approved reviews exist), approved
// review list, and an always-visible "Write a review" link.
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Star } from 'lucide-react';
import { API_BASE } from '../../lib/apiBase';

export function useProductReviews(handle) {
        const [data, setData] = useState({ count: 0, average: null, reviews: [] });
        useEffect(() => {
                let alive = true;
                fetch(`${API_BASE}/api/reviews/${encodeURIComponent(handle)}`)
                        .then((r) => r.json())
                        .then((j) => { if (alive && j?.ok) setData(j); })
                        .catch(() => {});
                return () => { alive = false; };
        }, [handle]);
        return data;
}

export function Stars({ value, size = 'h-4 w-4' }) {
        return (
                <span className="inline-flex gap-0.5" aria-label={`${value} out of 5 stars`}>
                        {[1, 2, 3, 4, 5].map((i) => (
                                <Star key={i} className={`${size} ${i <= Math.round(value) ? 'fill-[#FF9F1C] text-[#FF9F1C]' : 'text-white/25'}`} />
                        ))}
                </span>
        );
}

export const writeReviewHref = (handle) => `/write-review?product=${encodeURIComponent(handle)}`;

export function ReviewSummary({ handle, summary }) {
        return (
                <div data-testid="pdp-review-summary" className="mt-3 flex items-center gap-3 flex-wrap">
                        {summary.count > 0 && (
                                <a href="#pdp-reviews" data-testid="pdp-review-stars" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
                                        <Stars value={summary.average} />
                                        <span className="font-mono text-[11px] tracking-[0.12em] text-white/60">
                                                {summary.average} · {summary.count} review{summary.count > 1 ? 's' : ''}
                                        </span>
                                </a>
                        )}
                        <Link
                                to={writeReviewHref(handle)}
                                data-testid="pdp-write-review-link"
                                className="font-mono text-[10px] tracking-[0.16em] uppercase text-[#FF9F1C] hover:underline"
                        >
                                Write a review →
                        </Link>
                </div>
        );
}

export function ReviewsList({ handle, summary }) {
        return (
                <section id="pdp-reviews" data-testid="pdp-reviews-section" className="mt-10 pt-8 border-t border-white/10 max-w-[900px]">
                        <div className="flex items-end justify-between gap-4 flex-wrap">
                                <div>
                                        <p className="font-mono text-[11px] tracking-[0.18em] uppercase text-[#FF9F1C]">Operator reviews</p>
                                        {summary.count > 0 && (
                                                <div className="mt-2 flex items-center gap-3">
                                                        <Stars value={summary.average} size="h-5 w-5" />
                                                        <span className="font-display text-[20px]">{summary.average}</span>
                                                        <span className="font-mono text-[11px] text-white/50">({summary.count})</span>
                                                </div>
                                        )}
                                </div>
                                <Link
                                        to={writeReviewHref(handle)}
                                        data-testid="pdp-reviews-write-button"
                                        className="font-mono text-[11px] tracking-[0.2em] uppercase border border-white/25 px-4 py-2.5 hover:border-[#FF9F1C] hover:text-[#FF9F1C] transition-colors"
                                >
                                        Write a review
                                </Link>
                        </div>
                        {summary.count === 0 ? (
                                <p data-testid="pdp-reviews-empty" className="mt-4 font-sans text-[13px] text-white/50">
                                        Own this? Be the first operator to review it.
                                </p>
                        ) : (
                                <div className="mt-6 space-y-5">
                                        {summary.reviews.map((r) => (
                                                <article key={r.id} data-testid="pdp-review-item" className="border-l-2 border-[#FF9F1C]/60 pl-4">
                                                        <div className="flex items-center gap-3">
                                                                <Stars value={r.rating} size="h-3.5 w-3.5" />
                                                                <span className="font-mono text-[11px] uppercase tracking-[0.12em] text-white/60">{r.author_name}</span>
                                                        </div>
                                                        {r.title && <p className="mt-2 font-display text-[15px] uppercase">{r.title}</p>}
                                                        <p className="mt-1 font-sans text-[13px] text-white/75 leading-relaxed whitespace-pre-line">{r.body}</p>
                                                        {r.images?.length > 0 && (
                                                                <div data-testid="pdp-review-images" className="mt-3 flex flex-wrap gap-2">
                                                                        {r.images.map((src) => (
                                                                                <a key={src} href={`${API_BASE}${src}`} target="_blank" rel="noreferrer" className="block h-20 w-20 overflow-hidden border border-white/15 hover:border-[#FF9F1C] transition-colors">
                                                                                        <img src={`${API_BASE}${src}`} alt={`Photo from ${r.author_name}`} loading="lazy" className="h-full w-full object-cover" />
                                                                                </a>
                                                                        ))}
                                                                </div>
                                                        )}
                                                </article>
                                        ))}
                                </div>
                        )}
                </section>
        );
}
